<template>
	<Container class="admin">
		<Card class="admin__section">
			<template #title>Admin Tools</template>
			<template #content>
				<div class="admin__actions">
					<SendEmailDownloadButton
						v-if="detailsState.details.value?.id"
						:app-id="detailsState.details.value.id" />
					<Button
						v-if="auth.hasFeatureFlag('email-delivery')"
						icon="fal fa-clock-rotate-left"
						label="Email history"
						outlined
						severity="secondary"
						@click="openEmailHistory" />
					<Button
						v-if="activeBuild && !activeBuild.draft"
						icon="fal fa-gears"
						label="Rebuild Version"
						outlined
						severity="secondary"
						@click="rebuildVersion" />
					<Button
						v-if="activeBuild && activeBuild.draft === false && isDev"
						icon="fal fa-terminal"
						label="Copy start command"
						outlined
						severity="secondary"
						@click="copyStartCommand" />
					<Button
						v-if="activeBuild && activeBuild.draft === false && isDev"
						icon="fal fa-folder"
						label="Copy path"
						outlined
						severity="secondary"
						@click="copyPathCommand" />
				</div>
			</template>
		</Card>

		<Card class="admin__section">
			<template #title>JSON Schema</template>
			<template #content>
				<AppSchemaPanel
					v-if="activeBuild && app"
					:app
					:build="activeBuild" />
			</template>
		</Card>
	</Container>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import Button from 'primevue/button'
import Card from 'primevue/card'
import { useToast } from 'primevue/usetoast'
import BuildsApi from '@/models/Build/Api'
import type { AppDetailsState } from '@/models/App/States'
import { EnrichedApp } from '@codecannon/schemas/schemas/EnrichedApp'
import AppSchemaPanel from './Admin/AppSchemaPanel.vue'
import Container from '@/components/Container.vue'
import SendEmailDownloadButton from '@/views/Admin/EmailDownloads/components/SendEmailDownloadButton.vue'
import { useAuthStore } from '@/stores/Auth'

const props = defineProps<{ detailsState: AppDetailsState; app: EnrichedApp | null }>()

const emit = defineEmits<{ (e: 'get-details'): void }>()

const detailsState = props.detailsState

const isDev = import.meta.env.DEV
const toast = useToast()
const auth = useAuthStore()
const router = useRouter()

const activeBuild = computed(() => detailsState.details.value?.latest_build)
const activeBuildId = computed(() => activeBuild.value?.id)

async function rebuildVersion() {
	if (!activeBuildId.value) return
	// Create a draft from latest, then finalize it (same behavior as EditApp)
	await new BuildsApi().update(activeBuildId.value, { draft: false })
	emit('get-details')
}

function openEmailHistory() {
	router.push({
		name: 'admin-email-downloads',
		query: { app_id: detailsState.details.value?.id },
	})
}

function copyStartCommand() {
	if (!activeBuildId.value) return
	navigator.clipboard.writeText(
		`make start-app APP=${detailsState.details.value?.id} BUILD=${activeBuildId.value}`,
	)
	toast.add({
		severity: 'info',
		summary: 'Command copied',
		detail: `Added \`make start-app APP=${detailsState.details.value?.id} BUILD=${activeBuildId.value}\` to clipboard`,
		life: 3000,
	})
}

function copyPathCommand() {
	if (!activeBuildId.value) return
	navigator.clipboard.writeText(
		`cd generator/generated/deploy/${auth.user!.id}/${detailsState.details.value?.id}/${activeBuildId.value}`,
	)
	toast.add({
		severity: 'info',
		summary: 'Command copied',
		detail: `Added \`cd generator/generated/deploy/${auth.user!.id}/${detailsState.details.value?.id}/${activeBuildId.value}\` to clipboard`,
		life: 3000,
	})
}
</script>

<style scoped lang="scss">
.admin {
	display: flex;
	flex-direction: column;
	gap: 16px;

	.admin__actions {
		display: flex;
		gap: 10px;
		flex-wrap: wrap;
	}
}
</style>
